/**
 * POS Product Grid Component
 *
 * Displays products filtered by category/animal type selection.
 * Shows curated "Top Sellers" rows on the home screen (no category selected),
 * and the full filtered grid when a category is selected.
 * Color-coded by category with prominent pricing.
 */

import React, { useMemo, useState } from 'react';
import { POSProduct } from '../../services/storeAPI';
import { getCategoryColor } from '../../constants/posColors';
import { detectAnimalType, FEED_CATEGORY_FAMILIES, FEED_FAMILIES, categoryInFamily, categoryMatches } from '../../constants/posCategoryFamilies';
import { NavState } from './POSCategoryNav';

// TODO: Replace hardcoded keywords with dynamic top sellers
// from POS transaction history once sufficient sales data
// exists. Query POSTransactionItem grouped by product,
// ordered by quantity sold, filtered by category.
interface CuratedRow {
  label: string;
  categoryMatch: string;
  nameKeywords: string[];
  skuPrefix?: string;
}

const CURATED_ROWS: CuratedRow[] = [
  { label: 'BYOC 5 STAR', categoryMatch: 'BYOC 5 STAR',
    nameKeywords: ['winter layer', 'scratch', 'all flock mash'] },
  { label: '5 STAR Bags', categoryMatch: '5 STAR Bags',
    nameKeywords: ['winter layer', 'scratch', 'all flock'],
    skuPrefix: 'FST-' },
  { label: 'Dog Food', categoryMatch: 'DOG',
    nameKeywords: ['lamb and rice', 'beef and rice', 'large breed', 'chicken and rice', 'high prairie'] },
  { label: 'Cat Food', categoryMatch: 'CAT',
    nameKeywords: ['diamond naturals', 'taste of the wild'] },
];

interface POSProductGridProps {
  products: POSProduct[];
  navState: NavState;
  onAddToCart: (product: POSProduct) => void;
  loading?: boolean;
}

const POSProductGrid: React.FC<POSProductGridProps> = ({
  products,
  navState,
  onAddToCart,
  loading = false,
}) => {
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);

  const isHomeScreen = navState.level === 'family' && !navState.selectedFamily;

  // Build curated rows (only computed when on home screen)
  const curatedRows = useMemo(() => {
    if (!isHomeScreen) return [];
    return CURATED_ROWS.map((row) => {
      const matchedProducts = products.filter((p) => {
        const catLower = (p.category_name || '').toLowerCase();
        if (catLower !== row.categoryMatch.toLowerCase()) return false;
        const nameLower = p.name.toLowerCase();
        if (!row.nameKeywords.some((kw) => nameLower.includes(kw))) return false;
        if (row.skuPrefix && !(p.sku || '').startsWith(row.skuPrefix)) return false;
        return true;
      });
      return { ...row, products: matchedProducts };
    }).filter((row) => row.products.length > 0);
  }, [isHomeScreen, products]);

  // Filter products based on navigation state (for non-home screens)
  const filteredProducts = useMemo(() => {
    if (isHomeScreen) return []; // Not used on home screen
    return products.filter((product) => {
      // Filter by family (check if category belongs to selected family)
      if (navState.selectedFamily) {
        const familyData = FEED_FAMILIES[navState.selectedFamily];
        if (!familyData) return false;
        if (!categoryInFamily(product.category_name, familyData.categories)) {
          return false;
        }
      }

      // Filter by specific category (normalized matching)
      if (navState.selectedCategory && !categoryMatches(product.category_name, navState.selectedCategory)) {
        return false;
      }

      // Filter by animal type (only for feed categories AND only if specific type selected)
      if (
        navState.selectedAnimalType &&
        navState.selectedAnimalType !== 'All' &&
        navState.selectedFamily &&
        FEED_CATEGORY_FAMILIES.includes(navState.selectedFamily)
      ) {
        const productAnimalType = detectAnimalType(product.name);
        if (productAnimalType !== navState.selectedAnimalType) {
          return false;
        }
      }

      return true;
    });
  }, [isHomeScreen, products, navState]);

  const renderProductTile = (product: POSProduct) => {
    const colors = getCategoryColor(product.category_name);
    const isHovered = hoveredProduct === product.id;

    return (
      <button
        key={product.id}
        onClick={() => onAddToCart(product)}
        style={{
          ...styles.productButton,
          backgroundColor: isHovered ? colors.hover : colors.bg,
          color: colors.text,
        }}
        onMouseEnter={() => setHoveredProduct(product.id)}
        onMouseLeave={() => setHoveredProduct(null)}
      >
        <span style={styles.productName}>{product.name}</span>
        <span style={styles.productPrice}>
          ${parseFloat(product.effective_price || product.retail_price).toFixed(2)}
          {product.is_byoc && <span style={styles.byocTag}>/lb</span>}
        </span>
        {product.sku && (
          <span style={{ ...styles.productSku, color: colors.text }}>
            {product.sku}
          </span>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading products...</div>
      </div>
    );
  }

  // Home screen: show curated rows
  if (isHomeScreen) {
    if (curatedRows.length === 0) {
      return (
        <div style={styles.emptyContainer}>
          <div style={styles.emptyText}>No products found</div>
          <div style={styles.emptySubtext}>
            Try selecting a different category or animal type
          </div>
        </div>
      );
    }

    return (
      <div style={styles.curatedContainer}>
        <div style={styles.curatedHeader}>Top Sellers</div>
        {curatedRows.map((row) => {
          const colors = getCategoryColor(row.categoryMatch);
          return (
            <div key={row.label} style={styles.curatedRow}>
              <div style={styles.curatedRowHeader}>
                <div style={{ ...styles.curatedRowHeaderStrip, backgroundColor: colors.bg }} />
                <span style={styles.curatedRowLabel}>{row.label}</span>
                <span style={styles.curatedRowCount}>
                  {row.products.length} item{row.products.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={styles.curatedRowGrid}>
                {row.products.map(renderProductTile)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Category selected: show filtered flat grid
  if (filteredProducts.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyText}>No products found</div>
        <div style={styles.emptySubtext}>
          Try selecting a different category or animal type
        </div>
      </div>
    );
  }

  return (
    <div style={styles.gridContainer}>
      <div style={styles.productCount}>
        {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
      </div>
      <div style={styles.grid}>
        {filteredProducts.map(renderProductTile)}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  gridContainer: {
    flex: 1,
    overflow: 'auto',
  },
  productCount: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '6px',
    paddingLeft: '4px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '8px',
  },
  productButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 8px',
    minHeight: '85px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.15s, transform 0.1s',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    textAlign: 'center',
  },
  productName: {
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: '1.3',
    marginBottom: '4px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  productPrice: {
    fontSize: '16px',
    fontWeight: '700',
  },
  byocTag: {
    fontSize: '12px',
    fontWeight: '500',
    marginLeft: '2px',
  },
  productSku: {
    fontSize: '10px',
    opacity: 0.7,
    marginTop: '2px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
  },
  loadingText: {
    fontSize: '18px',
    color: '#6B7280',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    padding: '24px',
  },
  emptyText: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#4B5563',
    marginBottom: '8px',
  },
  emptySubtext: {
    fontSize: '16px',
    color: '#9CA3AF',
  },
  // Curated rows (home screen)
  curatedContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '0 4px',
  },
  curatedHeader: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: '12px',
  },
  curatedRow: {
    marginBottom: '16px',
  },
  curatedRowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  curatedRowHeaderStrip: {
    width: '4px',
    height: '20px',
    borderRadius: '2px',
  },
  curatedRowLabel: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#1F2937',
  },
  curatedRowCount: {
    fontSize: '13px',
    color: '#6B7280',
  },
  curatedRowGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
};

export default POSProductGrid;
